import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { euro, useCart } from "@/cart";
import { resolveSrc } from "@/components/Gallery";

const API = process.env.REACT_APP_BACKEND_URL;

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 } }),
};

function ProductCard({ product, index }) {
  const { add } = useCart();
  const [size, setSize] = useState("");
  const needsSize = product.sizes.length > 0;

  const addToCart = () => {
    if (needsSize && !size) {
      toast("Scegli una taglia", { description: product.name });
      return;
    }
    add(product, size);
    toast("Aggiunto al carrello", { description: `${product.name}${size ? ` · ${size}` : ""}` });
  };

  return (
    <motion.article
      variants={reveal}
      custom={index % 4}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      data-testid={`product-card-${index + 1}`}
      className="group flex flex-col border border-neutral-800 bg-[#0D0D0D]"
    >
      <div className="relative aspect-square overflow-hidden border-b border-neutral-800">
        <img src={resolveSrc(product.image)} alt={product.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
        <span className="absolute left-3 top-3 bg-black/70 px-2 py-1 font-mono-tac text-xs tracking-[0.2em] text-neutral-100">{euro(product.price_cents)}</span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-serif-logo text-base font-bold tracking-wide text-neutral-100">{product.name}</h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-neutral-400">{product.description}</p>
        {needsSize && (
          <div className="mt-4 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Taglia">
            {product.sizes.map((s) => (
              <button
                key={s}
                role="radio"
                aria-checked={size === s}
                data-testid={`product-${index + 1}-size-${s}`}
                onClick={() => setSize(s)}
                className={`h-8 min-w-[2.25rem] border px-2 font-mono-tac text-[11px] tracking-[0.15em] transition-colors duration-150 ${
                  size === s ? "border-[#C8102E] bg-red-950/30 text-neutral-100" : "border-neutral-700 text-neutral-400 hover:border-red-700 hover:text-red-500"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <button
          data-testid={`product-${index + 1}-add`}
          onClick={addToCart}
          className="mt-5 inline-flex h-11 items-center justify-center gap-2 border border-neutral-700 font-mono-tac text-xs uppercase tracking-[0.2em] text-neutral-100 transition-all duration-200 hover:border-[#C8102E] hover:bg-[#C8102E] hover:text-white"
        >
          <ShoppingBag className="h-4 w-4" /> Aggiungi
        </button>
      </div>
    </motion.article>
  );
}

export default function Merch() {
  const [products, setProducts] = useState([]);
  const [shipping, setShipping] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/products`).then((r) => (r.ok ? r.json() : [])).then(setProducts).catch(() => setProducts([]));
    fetch(`${API}/api/shop/config`).then((r) => (r.ok ? r.json() : null)).then((c) => setShipping(c?.shipping_cents ?? null)).catch(() => {});
  }, []);

  if (products.length === 0) return null;

  return (
    <section id="merch" data-testid="merch-section" className="relative border-t border-neutral-900 bg-[#090909]">
      <div className="pointer-events-none absolute -left-40 top-20 h-[28rem] w-[28rem] bg-[radial-gradient(circle,rgba(200,16,46,0.14),transparent_65%)] blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36">
        <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <motion.p variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} className="font-mono-tac text-xs uppercase tracking-[0.35em] text-red-600">
              07 — Merch
            </motion.p>
            <motion.h2 variants={reveal} custom={1} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} className="font-display mt-4 text-5xl uppercase leading-none text-neutral-100 sm:text-7xl">
              Portati a casa <span className="text-outline-red">il rumore</span>
            </motion.h2>
          </div>
          <motion.p variants={reveal} custom={2} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} className="max-w-md text-sm leading-relaxed text-neutral-400">
            Pagamento sicuro con carta tramite Stripe.
            {shipping !== null && ` Spedizione in Italia ${euro(shipping)}, 3–7 giorni lavorativi.`}
          </motion.p>
        </div>
        <div data-testid="merch-grid" className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
